import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Cancel,
  CalendarToday,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  EmojiEvents,
  AttachMoney,
  Description,
  Schedule,
  CheckCircle,
  Warning,
  Info,
  Calculate,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Scale,
  Speed,
  MonetizationOn,
  LocalAtm,
  AccountBalanceWallet,
  SwapHoriz,
  ExpandMore,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getGoldsmiths } from '../redux/actions/goldsmithActions';
import { getCustomerOrders } from '../redux/actions/goldsmithActions';
import { getTodayGoldRate, createGoldsmithPurchase } from '../redux/actions/goldsmithActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const NewGoldsmithPurchase = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { goldsmiths, goldsmithsLoading } = useSelector(state => state.goldsmith);
  const { customerOrders, ordersLoading } = useSelector(state => state.goldsmith);
  const { goldRate, goldRateLoading } = useSelector(state => state.goldsmith);
  const { purchasesLoading } = useSelector(state => state.goldsmith);
  const { activePaymentModes, loading: paymentModesLoading } = useSelector(state => state.paymentModes || {});

  const [formData, setFormData] = useState({
    goldsmith_id: '',
    customer_order_id: '',
    product_name: '',
    description: '',
    net_weight: '',
    touch: '',
    gold_rate: '',
    pure_amount: '',
    mc_wastage_amount: '',
    total_amount: '',
    purchase_date: new Date().toISOString().split('T')[0], // Today's date
    payment_mode: 'pure_only',
    payment_details: {
      pure_given: '',
      amount_paid: '',
      balance_pure: '',
      balance_amount: ''
    },
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [useCustomGoldRate, setUseCustomGoldRate] = useState(false);
  const [paymentEntries, setPaymentEntries] = useState([{ payment_mode_code: '', amount: '', reference_number: '' }]);
  const [items, setItems] = useState([
    { product_name: '', description: '', net_weight: '', touch: '', mc_amount: '' }
  ]);
  const [openNewProductDialog, setOpenNewProductDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [newProductDraft, setNewProductDraft] = useState({ product_name: '', description: '', net_weight: '', touch: '', mc_amount: '' });

  // Style to prevent strikethrough on labels
  const labelStyle = {
    textDecoration: 'none',
    '&.Mui-disabled': {
      color: 'text.primary',
      textDecoration: 'none'
    }
  };

  useEffect(() => {
    dispatch(getGoldsmiths());
    dispatch(getCustomerOrders());
    dispatch(getTodayGoldRate());
    dispatch(getActivePaymentModes());
  }, [dispatch]);

  useEffect(() => {
    if (goldRate && !useCustomGoldRate) {
      setFormData(prev => ({
        ...prev,
        gold_rate: goldRate.rate_per_gram || ''
      }));
    }
  }, [goldRate, useCustomGoldRate]);

  const accordionSections = [
    { title: 'Purchase Details', color: 'primary' },
    { title: 'Payment & Review', color: 'warning' }
  ];

  // Accordions removed to match Billing layout

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Auto-populate product details when customer order is selected
      if (field === 'customer_order_id') {
        const selectedOrder = customerOrders.find(order => order.id === parseInt(value));
        if (selectedOrder) {
          // Pre-populate the first item from order estimates
          setItems(prev => {
            const next = [...prev];
            next[0] = {
              ...(next[0] || {}),
              product_name: selectedOrder.design_details || '',
              description: selectedOrder.product_description || '',
              net_weight: selectedOrder.estimated_weight ? selectedOrder.estimated_weight.toString() : '',
              touch: selectedOrder.estimated_touch ? selectedOrder.estimated_touch.toString() : '',
              mc_amount: next[0]?.mc_amount || ''
            };
            return next;
          });
        } else {
          // Clear first item if no order selected
          setItems([{ product_name: '', description: '', net_weight: '', touch: '', mc_amount: '' }]);
        }
      }

      return newData;
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };

  const addItemRow = () => setItems(prev => [...prev, { product_name: '', description: '', net_weight: '', touch: '', mc_amount: '' }]);
  const removeItemRow = (index) => setItems(prev => prev.filter((_, i) => i !== index));
  const handleSaveNewProduct = () => {
    setItems(prev => [{
      product_name: newProductDraft.product_name || '',
      description: newProductDraft.description || '',
      net_weight: newProductDraft.net_weight || '',
      touch: newProductDraft.touch || '',
      mc_amount: newProductDraft.mc_amount || ''
    }, ...prev]);
    setNewProductDraft({ product_name: '', description: '', net_weight: '', touch: '', mc_amount: '' });
    setOpenNewProductDialog(false);
  };

  const handleCancelPurchase = () => {
    // Reset all form data
    setFormData({
      goldsmith_id: '',
      customer_order_id: '',
      product_name: '',
      description: '',
      net_weight: '',
      touch: '',
      pure_amount: '',
      estimated_amount: '',
      actual_weight: '',
      actual_touch: '',
      actual_amount: '',
      making_charges: '',
      total_amount: '',
      payment_method: 'cash',
      payment_reference: '',
      payment_bank_details: '',
      notes: ''
    });
    
    // Navigate back to purchases list
    navigate('/goldsmith-purchases');
  };

  const handlePaymentDetailChange = (field, value) => {
    setFormData(prev => {
      const newPaymentDetails = {
        ...prev.payment_details,
        [field]: value
      };

      // Auto-calculate balances from totals
      const totalPure = parseFloat(prev.pure_amount) || 0;
      const paidPure = parseFloat(field === 'pure_given' ? value : prev.payment_details.pure_given) || 0;
      const goldRate = parseFloat(prev.gold_rate) || 0;
      const totalAmountDue = parseFloat(calculateTotalAmount()) || 0;
      const sumFromEntries = paymentEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
      const totalPaidAmount = field === 'amount_paid'
        ? (parseFloat(value) || 0)
        : (sumFromEntries > 0 ? sumFromEntries : (parseFloat(prev.payment_details.amount_paid) || 0));

      const balancePure = (totalPure - paidPure);
      const balanceAmount = Math.max(0, totalAmountDue - (paidPure * goldRate) - totalPaidAmount);

      newPaymentDetails.balance_pure = balancePure.toFixed(3);
      newPaymentDetails.balance_amount = balanceAmount.toFixed(2);

      return {
        ...prev,
        payment_details: newPaymentDetails
      };
    });
  };

  const handlePaymentEntryChange = (index, field, value) => {
    setPaymentEntries(prev => {
      const next = prev.map((row, i) => i === index ? { ...row, [field]: value } : row);
      // Recompute balances based on new amount sum
      const amountSum = next.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      handlePaymentDetailChange('amount_paid', amountSum.toString());
      return next;
    });
  };

  const handlePaymentModeToggle = (mode) => {
    setPaymentEntries(prev => {
      const code = mode.code || mode.name;
      const exists = prev.find(e => e.payment_mode_code === code);
      const next = exists
        ? prev.filter(e => e.payment_mode_code !== code)
        : [...prev, { payment_mode_code: code, amount: '', reference_number: '', bank_details: '' }];
      const amountSum = next.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      handlePaymentDetailChange('amount_paid', amountSum.toString());
      return next;
    });
  };

  // Derive a consistent color per payment mode based on its code/name
  const getPaymentModeColor = (mode) => {
    const key = ((mode && (mode.code || mode.name)) || '').toString().toLowerCase();
    if (key.includes('cash')) return 'success';
    if (key.includes('upi') || key.includes('gpay') || key.includes('phonepe') || key.includes('paytm')) return 'info';
    if (key.includes('card') || key.includes('debit') || key.includes('credit card')) return 'secondary';
    if (key.includes('bank') || key.includes('neft') || key.includes('rtgs') || key.includes('imps')) return 'warning';
    if (key.includes('cheque') || key.includes('check')) return 'warning';
    if (key.includes('credit') || key.includes('due') || key.includes('ledger')) return 'error';
    return 'primary';
  };

  const addPaymentEntry = () => {
    setPaymentEntries(prev => [...prev, { payment_mode_code: '', amount: '', reference_number: '' }]);
  };

  const removePaymentEntry = (index) => {
    setPaymentEntries(prev => {
      const next = prev.filter((_, i) => i !== index);
      const amountSum = next.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      handlePaymentDetailChange('amount_paid', amountSum.toString());
      return next;
    });
  };

  const getTotalPaidAmount = () => paymentEntries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  const getRemainingAmount = () => {
    const totalAmount = parseFloat(calculateTotalAmount()) || 0;
    const pureGiven = parseFloat(formData.payment_details.pure_given || '0') || 0;
    const goldRate = parseFloat(formData.gold_rate || '0') || 0;
    const paid = getTotalPaidAmount();
    return totalAmount - (pureGiven * goldRate) - paid;
  };

  const calculatePureWeight = () => {
    if (items.length > 0) {
      const total = items.reduce((s, it) => s + ((parseFloat(it.net_weight || 0) * (parseFloat(it.touch || 0))) / 100), 0);
      return total.toFixed(3);
    }
    if (formData.net_weight && formData.touch) {
      return (parseFloat(formData.net_weight) * parseFloat(formData.touch) / 100).toFixed(3);
    }
    return '0.000';
  };

  const calculatePureAmount = () => {
    const goldRateNum = parseFloat(formData.gold_rate || 0) || 0;
    if (items.length > 0) {
      const totalPure = parseFloat(calculatePureWeight()) || 0;
      return (totalPure * goldRateNum).toFixed(2);
    }
    if (formData.pure_amount && formData.gold_rate) {
      return (parseFloat(formData.pure_amount) * goldRateNum).toFixed(2);
    }
    return '0.00';
  };

  const calculateTotalAmount = () => {
    const goldRateNum = parseFloat(formData.gold_rate || 0) || 0;
    if (items.length > 0) {
      const itemsTotal = items.reduce((sum, it) => {
        const netW = parseFloat(it.net_weight || 0) || 0;
        const touch = parseFloat(it.touch || 0) || 0;
        const pureW = (netW * touch) / 100;
        const pureVal = pureW * goldRateNum;
        const mc = parseFloat(it.mc_amount || 0) || 0;
        return sum + pureVal + mc;
      }, 0);
      const other = parseFloat(formData.mc_wastage_amount || 0) || 0;
      return (itemsTotal + other).toFixed(2);
    }
    const pureAmount = parseFloat(calculatePureAmount()) || 0;
    const mcAmount = parseFloat(formData.mc_wastage_amount) || 0;
    return (pureAmount + mcAmount).toFixed(2);
  };

  const validateAccordion = (accordionIndex) => {
    const newErrors = {};
    
    switch (accordionIndex) {
      case 0: // Goldsmith Selection
        if (!formData.goldsmith_id) {
          newErrors.goldsmith_id = 'Please select a goldsmith';
        }
        break;
      case 1: // Product Details
        if (items.length === 0) {
          newErrors.items = 'Add at least one item';
        }
        items.forEach((it, idx) => {
          if (!it.net_weight) newErrors[`item_${idx}_net_weight`] = 'Required';
          if (!it.touch) newErrors[`item_${idx}_touch`] = 'Required';
        });
        break;
      case 2: // Pure Calculation
        if (!formData.gold_rate) {
          newErrors.gold_rate = 'Gold rate is required';
        }
        break;
      case 3: // Payment Details
        if (formData.payment_mode === 'pure_only' && !formData.pure_amount) {
          newErrors.pure_amount = 'Pure amount is required';
        }
        if (formData.payment_mode === 'amount_only' && !formData.mc_wastage_amount) {
          newErrors.mc_wastage_amount = 'MC/Wastage amount is required';
        }
        if (formData.payment_mode === 'mixed') {
          if (!formData.payment_details.pure_given) {
            newErrors.pure_given = 'Pure given is required';
          }
          if (!formData.payment_details.amount_paid) {
            newErrors.amount_paid = 'Amount paid is required';
          }
        }
        break;
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateAllAccordions = () => {
    let isValid = true;
    for (let i = 0; i < accordionSections.length - 1; i++) { // Skip review accordion
      if (!validateAccordion(i)) {
        isValid = false;
      }
    }
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateAllAccordions()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const paidPure = parseFloat(formData.payment_details.pure_given || '0') || 0;
      const paidAmount = getTotalPaidAmount();
      let computedPaymentMode = 'amount_only';
      if (paidPure > 0 && paidAmount > 0) computedPaymentMode = 'mixed';
      else if (paidPure > 0) computedPaymentMode = 'pure_only';

      const purchaseData = {
        ...formData,
        payment_mode: computedPaymentMode,
        gold_rate: parseFloat(formData.gold_rate),
        total_amount: parseFloat(calculateTotalAmount()),
        pure_given: parseFloat(formData.payment_details.pure_given) || 0,
        amount_paid: getTotalPaidAmount(),
        payment_modes: paymentEntries.filter(e => e.payment_mode_code && parseFloat(e.amount) > 0),
        items: items.map(it => ({
          product_name: it.product_name || '',
          description: it.description || '',
          net_weight: parseFloat(it.net_weight) || 0,
          touch: parseFloat(it.touch) || 0,
          mc_amount: parseFloat(it.mc_amount) || 0
        })),
        // Map UI "MC/Wastage Amount" to a header-level making_charges for backend aggregation
        making_charges: parseFloat(formData.mc_wastage_amount) || 0
      };

      await dispatch(createGoldsmithPurchase(purchaseData));
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/goldsmith-purchases');
      }, 2000);
    } catch (error) {
      console.error('Error creating purchase:', error);
      alert('Error creating purchase: ' + error.message);
    }
  };

  const selectedGoldsmith = goldsmiths.find(g => g.id === formData.goldsmith_id);
  const selectedOrder = customerOrders.find(o => o.id === formData.customer_order_id);

  if (success) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Purchase Created Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The goldsmith purchase entry has been created and account updated.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/goldsmith-purchases')}
              startIcon={<ArrowBack />}
            >
              View All Purchases
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/goldsmith-purchases')}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Create New Goldsmith Purchase
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create a purchase entry for goldsmith work and update accounts
          </Typography>
        </Box>
      </Box>

      {/* Form Content - Billing-like two-column layout */}
      <Grid container spacing={3}>
        {/* Top Summary Cards (Billing-style) */}
        <Grid item xs={12}>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total Items</Typography>
                  <Typography variant="h5" fontWeight="bold">{items.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total Pure</Typography>
                  <Typography variant="h5" fontWeight="bold">{calculatePureWeight()} g</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Gold Rate</Typography>
                  <Typography variant="h5" fontWeight="bold">₹{formData.gold_rate || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Items Total</Typography>
                  <Typography variant="h5" fontWeight="bold">₹{calculateTotalAmount()}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Full-width Items Section (Billing-style) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Items</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={() => setOpenNewProductDialog(true)}>Add New Product</Button>
                  <Button variant="contained" size="small" onClick={addItemRow}>Add Item</Button>
                </Box>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Net Wt (g)</TableCell>
                      <TableCell>Touch (%)</TableCell>
                      <TableCell>MC (₹)</TableCell>
                      <TableCell>Pure (g)</TableCell>
                      <TableCell>Line Total (₹)</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ minWidth: 180 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={it.product_name}
                            onChange={(e) => handleItemChange(idx, 'product_name', e.target.value)}
                            placeholder="Item name"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={it.description}
                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </TableCell>
                        <TableCell sx={{ width: 130 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={it.net_weight}
                            onChange={(e) => handleItemChange(idx, 'net_weight', e.target.value)}
                            inputProps={{ step: '0.001', min: 0 }}
                            placeholder="0.000"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Scale /></InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 120 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={it.touch}
                            onChange={(e) => handleItemChange(idx, 'touch', e.target.value)}
                            inputProps={{ step: '0.1', min: 0, max: 100 }}
                            placeholder="0.0"
                            InputProps={{ startAdornment: <InputAdornment position="start"><TrendingUpIcon /></InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={it.mc_amount}
                            onChange={(e) => handleItemChange(idx, 'mc_amount', e.target.value)}
                            inputProps={{ step: '0.01', min: 0 }}
                            placeholder="0.00"
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 120 }}>
                          <Typography variant="body2">
                            {(((parseFloat(it.net_weight || 0) || 0) * (parseFloat(it.touch || 0) || 0)) / 100).toFixed(3)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const netW = parseFloat(it.net_weight || 0) || 0;
                              const touch = parseFloat(it.touch || 0) || 0;
                              const pureW = (netW * touch) / 100;
                              const pureVal = pureW * (parseFloat(formData.gold_rate || 0) || 0);
                              const mc = parseFloat(it.mc_amount || 0) || 0;
                              return `₹${(pureVal + mc).toFixed(2)}`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: 80 }}>
                          <IconButton size="small" color="error" onClick={() => removeItemRow(idx)} disabled={items.length === 1}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Total Pure</Typography>
                    <Typography variant="body1" fontWeight="bold">{calculatePureWeight()} g</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Gold Rate</Typography>
                    <Typography variant="body1" fontWeight="bold">₹{formData.gold_rate || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Items Total</Typography>
                    <Typography variant="body1" fontWeight="bold">₹{calculateTotalAmount()}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 4 }}>
          <Box sx={{}}>
          {/* Purchase Info */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business color="primary" />
              <Typography variant="h6" color="primary">Purchase Info</Typography>
              </Box>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.goldsmith_id}>
                    <InputLabel id="goldsmith-label" sx={labelStyle}>Select Goldsmith</InputLabel>
                    <Select
                      labelId="goldsmith-label"
                      label="Select Goldsmith"
                      size="small"
                      value={formData.goldsmith_id}
                      onChange={(e) => handleInputChange('goldsmith_id', e.target.value)}
                      disabled={goldsmithsLoading}
                    >
                      {goldsmiths.map((goldsmith) => (
                        <MenuItem key={goldsmith.id} value={goldsmith.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Business />
                            <Box>
                              <Typography variant="body1">{goldsmith.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {goldsmith.contact_person} • {goldsmith.phone}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.goldsmith_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errors.goldsmith_id}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="order-label" sx={labelStyle}>Related Customer Order (Optional)</InputLabel>
                    <Select
                      labelId="order-label"
                      label="Related Customer Order (Optional)"
                      size="small"
                      value={formData.customer_order_id}
                      onChange={(e) => handleInputChange('customer_order_id', e.target.value)}
                      disabled={ordersLoading}
                    >
                      <MenuItem value="">
                        <em>No related order</em>
                      </MenuItem>
                      {customerOrders.map((order) => (
                        <MenuItem key={order.id} value={order.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Schedule />
                            <Box>
                              <Typography variant="body1">{order.product_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Order #{order.order_number} • {order.customer?.name}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedGoldsmith && (
                <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Selected Goldsmith Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Name:</strong> {selectedGoldsmith.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Contact:</strong> {selectedGoldsmith.contact_person}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Phone:</strong> {selectedGoldsmith.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedGoldsmith.email}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              {/* --- Product Details (merged) --- */}
              <Box sx={{ mt: 3 }}>
                {/* Selected Order Details Display */}
                {formData.customer_order_id && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    icon={<Info />}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Order Details Auto-Populated
                    </Typography>
                    <Typography variant="body2">
                      Item details have been pre-filled from the selected customer order. You can modify them as needed.
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* --- Pure Calculation (merged) --- */}
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  {/* Items Table */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1">Items</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" size="small" onClick={() => setOpenNewProductDialog(true)}>Add New Product</Button>
                        <Button variant="contained" size="small" onClick={addItemRow}>Add Item</Button>
                      </Box>
                    </Box>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Net Wt (g)</TableCell>
                            <TableCell>Touch (%)</TableCell>
                            <TableCell>MC (₹)</TableCell>
                            <TableCell>Pure (g)</TableCell>
                            <TableCell>Line Total (₹)</TableCell>
                            <TableCell align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((it, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      size="small"
                      fullWidth
                                  value={it.product_name}
                                  onChange={(e) => handleItemChange(idx, 'product_name', e.target.value)}
                                  placeholder="Item name"
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      size="small"
                      fullWidth
                                  value={it.description}
                                  onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                                  placeholder="Description"
                                />
                              </TableCell>
                              <TableCell sx={{ width: 130 }}>
                  <TextField
                    size="small"
                      type="number"
                                  value={it.net_weight}
                                  onChange={(e) => handleItemChange(idx, 'net_weight', e.target.value)}
                                  inputProps={{ step: '0.001', min: 0 }}
                                  placeholder="0.000"
                                  InputProps={{ startAdornment: <InputAdornment position="start"><Scale /></InputAdornment> }}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 120 }}>
                  <TextField
                    size="small"
                      type="number"
                                  value={it.touch}
                                  onChange={(e) => handleItemChange(idx, 'touch', e.target.value)}
                                  inputProps={{ step: '0.1', min: 0, max: 100 }}
                                  placeholder="0.0"
                                  InputProps={{ startAdornment: <InputAdornment position="start"><TrendingUpIcon /></InputAdornment> }}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 140 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={it.mc_amount}
                                  onChange={(e) => handleItemChange(idx, 'mc_amount', e.target.value)}
                                  inputProps={{ step: '0.01', min: 0 }}
                                  placeholder="0.00"
                                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 120 }}>
                                <Typography variant="body2">
                                  {(((parseFloat(it.net_weight || 0) || 0) * (parseFloat(it.touch || 0) || 0)) / 100).toFixed(3)}
                      </Typography>
                              </TableCell>
                              <TableCell sx={{ width: 140 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {(() => {
                                    const netW = parseFloat(it.net_weight || 0) || 0;
                                    const touch = parseFloat(it.touch || 0) || 0;
                                    const pureW = (netW * touch) / 100;
                                    const pureVal = pureW * (parseFloat(formData.gold_rate || 0) || 0);
                                    const mc = parseFloat(it.mc_amount || 0) || 0;
                                    return `₹${(pureVal + mc).toFixed(2)}`;
                                  })()}
                      </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ width: 80 }}>
                                <IconButton size="small" color="error" onClick={() => removeItemRow(idx)} disabled={items.length === 1}>
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Total Pure</Typography>
                          <Typography variant="body1" fontWeight="bold">{calculatePureWeight()} g</Typography>
                  </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Gold Rate</Typography>
                          <Typography variant="body1" fontWeight="bold">₹{formData.gold_rate || 0}</Typography>
                </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Items Total</Typography>
                          <Typography variant="body1" fontWeight="bold">₹{calculateTotalAmount()}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Purchase Date"
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => handleInputChange('purchase_date', e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment> }}
                      InputLabelProps={{ shrink: true, sx: labelStyle }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Gold Rate per Gram (₹)"
                      type="number"
                      value={formData.gold_rate}
                      onChange={(e) => handleInputChange('gold_rate', e.target.value)}
                      error={!!errors.gold_rate}
                      helperText={errors.gold_rate}
                      InputProps={{ inputProps: { min: 0, step: 0.01 }, startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      disabled={!useCustomGoldRate}
                      InputLabelProps={{ sx: labelStyle }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Switch checked={useCustomGoldRate} onChange={(e) => setUseCustomGoldRate(e.target.checked)} />}
                      label="Use Custom Gold Rate"
                    />
                  </Grid>
                  {!useCustomGoldRate && goldRate && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Today's Gold Rate:</strong> ₹{goldRate.rate_per_gram} per gram
                        </Typography>
                        <Typography variant="caption">
                          Last updated: {new Date(goldRate.updated_at).toLocaleString()}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                  {/* Removed header-level pure amount and MC inputs; totals derived from items */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="subtitle2" gutterBottom color="success.main">
                        Amount Calculations
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2"><strong>Pure Amount:</strong> ₹{calculatePureAmount()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2"><strong>MC/Wastage:</strong> ₹{formData.mc_wastage_amount || '0.00'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2"><strong>Total Amount:</strong> ₹{calculateTotalAmount()}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
          </Box>

          {/* Removed extra accordions; merged into Purchase Details above */}

          {/* Payment & Review */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoney color="warning" />
              <Typography variant="h6" color="warning.main">Payment & Review</Typography>
              </Box>
              
              <Grid container spacing={3}>
                {/* Totals Row */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="warning.dark">Total Pure</Typography>
                        <Typography variant="h6">{calculatePureWeight()} grams</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="warning.dark">Total Amount</Typography>
                        <Typography variant="h6">₹{calculateTotalAmount()}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Paid/Balance Pure */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Paid Pure (grams)"
                    type="number"
                    value={formData.payment_details.pure_given}
                    onChange={(e) => handlePaymentDetailChange('pure_given', e.target.value)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.001 },
                      startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                    }}
                    InputLabelProps={{ sx: labelStyle }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Balance Pure (grams)"
                    type="number"
                    value={formData.payment_details.balance_pure}
                    disabled
                    InputProps={{
                      inputProps: { min: 0, step: 0.001 },
                      startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                    }}
                    InputLabelProps={{ sx: labelStyle }}
                  />
                </Grid>

                {/* Billing-style Payment Modes (Accordion per active mode) */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>Payments Modes</Typography>
                  <Box sx={{ mb: 2 }}>
                    {(activePaymentModes?.data || activePaymentModes || []).map((mode) => {
                      const codeKey = mode.code || mode.name;
                      const idx = paymentEntries.findIndex(e => e.payment_mode_code === codeKey);
                      const isSelected = idx !== -1;
                      const selected = isSelected ? paymentEntries[idx] : null;
                      const modeColor = getPaymentModeColor(mode);
                      const summaryBg = isSelected ? modeColor + '.light' : 'grey.50';
                      const summaryHoverBg = isSelected ? modeColor + '.light' : 'grey.100';
                      return (
                        <Accordion
                          key={codeKey}
                          expanded={isSelected}
                          onChange={() => handlePaymentModeToggle(mode)}
                          sx={{ mb: 1, borderLeft: '4px solid', borderLeftColor: modeColor + '.main' }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{ 
                              backgroundColor: summaryBg,
                              '&:hover': { backgroundColor: summaryHoverBg }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handlePaymentModeToggle(mode)}
                                onClick={(e) => e.stopPropagation()}
                                color={modeColor}
                              />
                              <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                {mode.name || mode.code}
                              </Typography>
                              {isSelected && selected?.amount && (
                                <Chip 
                                  label={`₹${selected.amount}`}
                                  size="small" 
                                  color={modeColor} 
                                  sx={{ ml: 'auto' }}
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {isSelected && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              size="small"
                                  fullWidth
                                  label="Amount (₹)"
                                  type="number"
                                  value={selected?.amount || ''}
                                  onChange={(e) => handlePaymentEntryChange(idx, 'amount', e.target.value)}
                                  placeholder="Enter amount"
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                  }}
                                  inputProps={{ step: '0.01', min: 0 }}
                                />
                                {mode.requires_reference && (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="Transaction Reference"
                                    value={selected?.reference_number || ''}
                                    onChange={(e) => handlePaymentEntryChange(idx, 'reference_number', e.target.value)}
                                    placeholder="Enter transaction reference number"
                                  />
                                )}
                                {mode.requires_bank && (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="Bank Details"
                                    value={selected?.bank_details || ''}
                                    onChange={(e) => handlePaymentEntryChange(idx, 'bank_details', e.target.value)}
                                    multiline
                                    rows={2}
                                    placeholder="Enter bank name, account number, etc."
                                  />
                                )}
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Paid Amount (Total)"
                        value={`₹${getTotalPaidAmount().toFixed(2)}`}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Balance Amount"
                        value={`₹${(parseFloat(formData.payment_details.balance_amount || '0') || 0).toFixed(2)}`}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    className="fullWidth-override"
                    size="small"
                    fullWidth
                    label="Additional Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Any additional notes or instructions..."
                    InputLabelProps={{ sx: labelStyle }}
                    sx={{ maxWidth: 'none', width: '100%' }}
                  />
                </Grid>
              </Grid>
          </Box>

          {/* Review content moved inside Payment & Review accordion above */}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleCancelPurchase}
              disabled={purchasesLoading}
              startIcon={<Cancel />}
              sx={{
                px: 4,
                py: 1.5,
                minWidth: 140
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={purchasesLoading}
              startIcon={purchasesLoading ? <CircularProgress size={20} /> : <Save />}
              sx={{
                background: 'linear-gradient(45deg, #FF9800 30%, #FFC107 90%)',
                boxShadow: '0 3px 5px 2px rgba(255, 152, 0, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #F57C00 30%, #FFB300 90%)',
                },
                px: 4,
                py: 1.5,
                minWidth: 140
              }}
            >
              {purchasesLoading ? 'Creating...' : 'Create Purchase'}
            </Button>
          </Box>
          </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Purchase Summary</Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Pure Amount</Typography>
                  <Typography variant="body2">₹{calculatePureAmount()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">MC/Wastage</Typography>
                  <Typography variant="body2">₹{formData.mc_wastage_amount || '0.00'}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">Grand Total</Typography>
                  <Typography variant="subtitle1">₹{calculateTotalAmount()}</Typography>
                </Box>
              </Box>

              {Math.abs(getRemainingAmount()) <= 0.01 ? (
                <Alert severity="success" sx={{ mb: 2 }}>Payment amount matches total</Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Payment total does not match. {getRemainingAmount() > 0 ? 'Add more payment.' : 'Reduce payment amount.'}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid Pure</Typography>
                <Typography variant="body2">{formData.payment_details.pure_given || 0} g</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid Amount</Typography>
                <Typography variant="body2">₹{getTotalPaidAmount().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Balance Amount</Typography>
                <Typography variant="body2">₹{Math.max(0, getRemainingAmount()).toFixed(2)}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelPurchase}
                  disabled={purchasesLoading}
                  startIcon={<Cancel />}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={purchasesLoading}
                  startIcon={purchasesLoading ? <CircularProgress size={20} /> : <Save />}
                  fullWidth
                >
                  {purchasesLoading ? 'Creating...' : 'Create Purchase'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add New Product Dialog (Billing-style minimal) */}
      <Dialog open={openNewProductDialog} onClose={() => setOpenNewProductDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                value={newProductDraft.product_name}
                onChange={(e) => setNewProductDraft(prev => ({ ...prev, product_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newProductDraft.description}
                onChange={(e) => setNewProductDraft(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Net Wt (g)"
                type="number"
                value={newProductDraft.net_weight}
                onChange={(e) => setNewProductDraft(prev => ({ ...prev, net_weight: e.target.value }))}
                inputProps={{ step: '0.001', min: 0 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Scale /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Touch (%)"
                type="number"
                value={newProductDraft.touch}
                onChange={(e) => setNewProductDraft(prev => ({ ...prev, touch: e.target.value }))}
                inputProps={{ step: '0.1', min: 0, max: 100 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><TrendingUpIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="MC (₹)"
                type="number"
                value={newProductDraft.mc_amount}
                onChange={(e) => setNewProductDraft(prev => ({ ...prev, mc_amount: e.target.value }))}
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewProductDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewProduct}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewGoldsmithPurchase;
